import { defineStore } from "pinia";
import { getCurrentUser, fetchAuthSession, fetchUserAttributes, updateUserAttributes } from "aws-amplify/auth";

import posthog from "posthog-js";

import {
	tiers,
	listedTiers,
	catalogEntryForTier,
	groupCode,
	CognitoAccountGroup,
	AccountGroupCode,
	ResolvedTierDetail,
	UserLimitsE,
	resolveTierDetail,
	allowanceFromTierLimits,
	allowanceFromUserRecord,
	MAPPED_USER_RECORD_ALLOWANCE_KEYS,
} from "@/js/tierconfig";
import { CoreClass, SERVED_CORE_CLASSES } from "@/types/api/cores";
import * as signaloidClient from "@/js/signaloidClient";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import {
	AccountLogsResponse,
	AWSAuthenticatedSession,
	AWSAuthenticatedUserObject,
	CurrentUser,
	User,
	OnboardingState,
	UserCustomization,
	UserPreferences,
} from "@/types/user";

import { SubscriptionConfig } from "@/types/api/subscriptions";
import { TierCatalogEntry } from "@/types/api/tiers";
import { monitoringCaptureError } from "@/plugins/monitoring";

const TIER_RESOLUTION_TRANSACTION = "Resolve current user tier details";
const USER_RECORD_TRANSACTION = "Fetch user object from DB";

function unresolvedTierMessage(cause: "static tier" | "catalog entry", tierCode: string, servedTier: string): string {
	return `Tier details unresolved: no ${cause} for code "${tierCode}", served as "${servedTier}"`;
}

export type MonthlyQuotaRow = {
	id: string;
	label: string;
	used: number;
	limit: number | null | undefined;
	unlimited: boolean;
	pct: number;
};

type State = {
	currentAuthenticatedUserObject: undefined | null | AWSAuthenticatedUserObject;
	currentUserObjectFromDB: undefined | User;
	currentUserObjectFetchInFlight: undefined | Promise<void>;
	currentUserSessionObject: undefined | null | AWSAuthenticatedSession;
	currentUserCustomizations: undefined | null | UserCustomization;
	currentUserTierCode: undefined | "" | AccountGroupCode;
	currentUserTierDetails: undefined | ResolvedTierDetail;
	currentUserSubscriptions: undefined | SubscriptionConfig;
	tierCatalog: undefined | TierCatalogEntry[];
	tierCatalogFetchInFlight: undefined | Promise<void>;
	tierResolutionFailed: boolean;
	reportedProblems: string[];
	currentUserActivityLogs: undefined | AccountLogsResponse;
	activityLogsLoading: boolean;
	activityLogsError: string | null;
};

export const useUserStore = defineStore("user", {
	state: (): State => ({
		currentAuthenticatedUserObject: undefined,
		currentUserObjectFromDB: undefined,
		currentUserObjectFetchInFlight: undefined,
		currentUserSessionObject: undefined,
		currentUserCustomizations: undefined,
		currentUserTierCode: undefined,
		currentUserTierDetails: undefined,
		currentUserSubscriptions: undefined,
		tierCatalog: undefined,
		tierCatalogFetchInFlight: undefined,
		tierResolutionFailed: false,
		reportedProblems: [],
		currentUserActivityLogs: undefined,
		activityLogsLoading: false,
		activityLogsError: null,
	}),
	getters: {
		currentUser(state): undefined | CurrentUser {
			//! deprecated
			if (!state.currentAuthenticatedUserObject) {
				return undefined;
			}
			const attributes = state.currentAuthenticatedUserObject?.attributes;
			return {
				email: attributes?.email ?? "",
				name: attributes?.name ?? "",
				affiliation: attributes?.["custom:affiliation"] ?? "",
				userID: attributes?.["custom:user_id"] ?? "",
				avatar: "",
			};
		},
		currentUserResourceUsage(state) {
			return state.currentUserObjectFromDB?.ResourceUsage;
		},
		// Per-quota usage for the monthly-resetting limits, shared by the home Quotas
		// tile and the "Upgrade Account" quick action. Returns [] while the tier
		// hasn't loaded so callers don't briefly read every quota as "unlimited".
		monthlyQuotaUsage(state): MonthlyQuotaRow[] {
			const usage = state.currentUserObjectFromDB?.ResourceUsage;
			if (!usage) return [];
			const allowance = state.currentUserTierDetails?.allowance;
			if (!allowance) return [];
			const defs: { id: string; label: string }[] = [
				{ id: UserLimitsE.DynamicInstructionCount, label: "Dynamic Instructions" },
				{ id: UserLimitsE.APIAccessCount, label: "API Requests" },
				{ id: UserLimitsE.PlotCount, label: "Distribution Plots" },
			];
			const usageMap = usage as unknown as Record<string, number | undefined>;
			const allowanceMap = allowance as unknown as Record<string, number | null | undefined>;
			return defs.map((d) => {
				const used = Number(usageMap[d.id] ?? 0);
				const limit = allowanceMap[d.id];
				const unlimited = limit === undefined;
				// `null` allowance means "not allowed" per tierconfig contract; treat
				// the same as a fully-consumed quota so alerts and upgrade CTA fire.
				const blocked = limit === null || limit === 0;
				const pct = unlimited ? 0 : blocked ? 1 : Math.min(1, used / Number(limit));
				return { id: d.id, label: d.label, used, limit, unlimited, pct };
			});
		},
		tierCatalogLoadFailed(state): boolean {
			return state.tierCatalog?.length === 0;
		},
		tierDetails(state): ResolvedTierDetail[] {
			return listedTiers.flatMap((tier) => {
				const catalogEntry = catalogEntryForTier(state.tierCatalog, tier.code);
				if (catalogEntry === undefined) {
					return [];
				}

				return [resolveTierDetail(tier, allowanceFromTierLimits(catalogEntry.Limits, SERVED_CORE_CLASSES))];
			});
		},
		currentUserPrimaryAtomicNetwork: (state) => {
			if (!state.currentUserCustomizations?.AtomicNetworks) {
				return "default";
			}

			return state.currentUserCustomizations?.AtomicNetworks?.length > 0
				? state.currentUserCustomizations?.AtomicNetworks[0]
				: "default";
		},
		currentUserPrimaryOrganization: (state): string | null => {
			if (!state.currentUserCustomizations?.Organizations) {
				return null;
			}
			return state.currentUserCustomizations?.Organizations?.length > 0
				? state.currentUserCustomizations?.Organizations[0]
				: null;
		},
	},
	actions: {
		reportProblemOnce(message: string, transactionName: string) {
			if (this.reportedProblems.includes(message)) {
				return;
			}

			this.reportedProblems.push(message);
			monitoringCaptureError(new Error(message), transactionName);
		},

		reportUserRecordShape(record: User) {
			const unknownAllowanceKeys = Object.keys(record.Allowance ?? {}).filter(
				(key) => !MAPPED_USER_RECORD_ALLOWANCE_KEYS.has(key),
			);
			if (unknownAllowanceKeys.length > 0) {
				this.reportProblemOnce(
					`User record served unknown allowance keys: ${unknownAllowanceKeys.join(", ")}`,
					USER_RECORD_TRANSACTION,
				);
			}

			if (record.Microarchitectures === undefined) {
				this.reportProblemOnce("User record served no microarchitectures", USER_RECORD_TRANSACTION);
			}

			if (record.CoreClasses === undefined) {
				this.reportProblemOnce("User record served no core classes", USER_RECORD_TRANSACTION);
			}
		},

		async refreshCurrentUserSession() {
			const newSession = await fetchAuthSession({ forceRefresh: true });
			this.currentUserSessionObject = newSession;
			await this.registerCurrentUserTokens();

			await this.fetchCurrentUserFromAWS();

			return this.currentUserSessionObject;
		},

		async registerCurrentUserTokens() {
			// This can be streamlined to
			signaloidClient.registerTokens(
				fetchAuthSession()
					.then((session) => session.tokens?.accessToken?.toString() ?? "")
					.catch((e) => {
						console.log("registerCurrentUserTokens - getAccessToken", e);
						return "";
					}),
				fetchAuthSession()
					.then((session) => session.tokens?.idToken?.toString() ?? "")
					.catch((e) => {
						console.log("registerCurrentUserTokens - getIdToken", e);
						return "";
					}),
			);

			signaloidClient.registerRefreshTokenCallbacks(
				async () => {
					const session = await fetchAuthSession({ forceRefresh: true });
					return session.tokens?.accessToken;
				},
				async () => {
					const session = await fetchAuthSession({ forceRefresh: true });
					return session.tokens?.idToken;
				},
			);
		},

		/*
		 * ======================================================
		 * Async functions for getting user data with dependencies
		 */

		async getCurrentAuthenticatedUserObject() {
			if (this.currentAuthenticatedUserObject === undefined) {
				await this.fetchCurrentUserFromAWS();
			}
			return this.currentAuthenticatedUserObject;
		},

		async getCurrentUserID() {
			if (this.currentAuthenticatedUserObject === undefined) {
				this.getCurrentAuthenticatedUserObject();
			}
			return new Promise<string>((resolve) => {
				const loop = () =>
					this.currentAuthenticatedUserObject === null ||
					this.currentAuthenticatedUserObject?.attributes !== undefined
						? resolve(this.currentAuthenticatedUserObject?.attributes?.["custom:user_id"] ?? "")
						: setTimeout(loop);
				loop();
			});
		},

		async getCurrentUserOrganizationID() {
			if (this.currentAuthenticatedUserObject === undefined) {
				await this.getCurrentAuthenticatedUserObject();
			}
			return this.currentAuthenticatedUserObject?.attributes?.["custom:organization_id"] ?? null;
		},
		async createOrganization(data: { Name: string; DedicatedInstanceName?: string }) {
			try {
				const response = await sdk.organizations.create(data);

				await this.refreshCurrentUserSession();

				return response;
			} catch (error) {
				monitoringCaptureError(error, "Create organization");
				console.log("Error creating organization :>> ", error);
				throw error;
			}
		},

		async getCurrentUserSubscriptions() {
			if (this.currentUserSubscriptions === undefined) {
				await this.fetchCurrentUserSubscriptions();
			}

			return new Promise<null | SubscriptionConfig>((resolve) => {
				const loop = () =>
					this.currentUserSubscriptions !== undefined
						? resolve(this.currentUserSubscriptions)
						: setTimeout(loop);
				loop();
			});
		},
		async fetchTierCatalog() {
			if (this.tierCatalog !== undefined && this.tierCatalog.length > 0) {
				return;
			}

			if (this.tierCatalogFetchInFlight !== undefined) {
				return this.tierCatalogFetchInFlight;
			}

			this.tierCatalogFetchInFlight = (async () => {
				try {
					this.tierCatalog = await signaloidClient.getTiers();
				} catch (error) {
					this.tierCatalog = [];
					monitoringCaptureError(error, "Fetch tier catalog");
				} finally {
					this.tierCatalogFetchInFlight = undefined;
				}
			})();

			return this.tierCatalogFetchInFlight;
		},

		async getCurrentUserTierDetails() {
			let tierCode;
			try {
				tierCode = await this.getCurrentUserTierCode();
			} catch {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = true;
				return undefined;
			}

			const staticTierDetails = tiers.find((e) => e.code === tierCode);

			await this.fetchTierCatalog();
			const catalogEntry = catalogEntryForTier(this.tierCatalog, tierCode);

			const servedTier = this.currentUserSubscriptions?.Tier;
			const accountCarriesATier = servedTier !== undefined && servedTier !== "NO_TIER";

			if (accountCarriesATier && staticTierDetails === undefined) {
				this.reportProblemOnce(
					unresolvedTierMessage("static tier", tierCode, servedTier),
					TIER_RESOLUTION_TRANSACTION,
				);
			}
			if (accountCarriesATier && catalogEntry === undefined && !this.tierCatalogLoadFailed) {
				this.reportProblemOnce(
					unresolvedTierMessage("catalog entry", tierCode, servedTier),
					TIER_RESOLUTION_TRANSACTION,
				);
			}

			if (staticTierDetails === undefined) {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = false;
				return undefined;
			}

			const isSignedIn =
				this.currentAuthenticatedUserObject !== undefined && this.currentAuthenticatedUserObject !== null;
			if (this.currentUserObjectFromDB === undefined && isSignedIn) {
				await this.fetchCurrentUserObjectFromDB().catch(() => undefined);
			}

			const userCoreClasses = this.currentUserObjectFromDB?.CoreClasses;
			const allowedCoreClasses =
				userCoreClasses === undefined
					? SERVED_CORE_CLASSES
					: userCoreClasses.map((coreClass) => coreClass.Name as CoreClass);
			const servedAllowance = this.currentUserObjectFromDB?.Allowance;
			const recordMicroArchitectures = this.currentUserObjectFromDB?.Microarchitectures;
			const allowedMicroArchitectures =
				recordMicroArchitectures?.map((microArchitecture) => microArchitecture.Name) ??
				catalogEntry?.Limits.CoreAvailableMicroarchitectures;
			const catalogAllowance =
				catalogEntry === undefined
					? undefined
					: allowanceFromTierLimits(catalogEntry.Limits, allowedCoreClasses);
			const allowance =
				servedAllowance !== undefined && allowedMicroArchitectures !== undefined
					? allowanceFromUserRecord(servedAllowance, allowedCoreClasses, allowedMicroArchitectures)
					: catalogAllowance;

			if (allowance === undefined) {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = true;
				return undefined;
			}

			this.currentUserTierDetails = resolveTierDetail(staticTierDetails, allowance);
			this.tierResolutionFailed = false;

			return this.currentUserTierDetails;
		},

		async getCurrentUserTierCode() {
			if (this.currentUserSubscriptions === undefined) {
				await this.fetchCurrentUserSubscriptions();
			}

			this.currentUserTierCode = groupCode(this.currentUserSubscriptions?.Tier);

			return this.currentUserTierCode;
		},

		async getCurrentUserGroups() {
			if (this.currentUserSessionObject === undefined) {
				this.fetchCurrentUserSessionFromAWS();
			}
			return new Promise<null | CognitoAccountGroup[]>((resolve) => {
				const loop = () => {
					if (this.currentUserSessionObject !== undefined) {
						if (this.currentUserSessionObject === null) {
							return resolve(null);
						} else {
							resolve(
								(this.currentUserSessionObject as any).tokens?.accessToken?.payload?.[
									"cognito:groups"
								] ?? null,
							);
						}
					} else {
						setTimeout(loop);
					}
				};
				loop();
			});
		},
		async getCurrentUserPreferences(): Promise<Partial<UserPreferences> | undefined> {
			if (this.currentUserObjectFromDB === undefined) {
				await this.fetchCurrentUserObjectFromDB().catch(() => undefined);
			}

			return this.currentUserObjectFromDB?.Preferences;
		},
		async getCurrentUserOnboardingState(): Promise<Partial<OnboardingState> | undefined> {
			if (this.currentUserObjectFromDB === undefined) {
				await this.fetchCurrentUserObjectFromDB();
			}

			return this.currentUserObjectFromDB?.OnboardingState;
		},
		async getCurrentUserCustomizations(): Promise<null | Partial<UserCustomization>> {
			if (this.currentUserCustomizations === undefined) {
				await this.fetchCurrentUserCustomizationsFromDB().catch(() => undefined);
			}

			return this.currentUserCustomizations ?? null;
		},
		async getCurrentUserPrimaryOrganization() {
			if (this.currentUserCustomizations === undefined) {
				this.fetchCurrentUserCustomizationsFromDB();
			}
			return new Promise<null | string>((resolve) => {
				const loop = () =>
					this.currentUserCustomizations !== undefined
						? resolve(this.currentUserPrimaryOrganization)
						: setTimeout(loop);
				loop();
			});
		},
		async getCurrentUserPrimaryAtomicNetwork() {
			if (this.currentUserCustomizations === undefined) {
				this.fetchCurrentUserCustomizationsFromDB();
			}
			return new Promise<null | string>((resolve) => {
				const loop = () =>
					this.currentUserCustomizations !== undefined
						? resolve(this.currentUserPrimaryAtomicNetwork)
						: setTimeout(loop);
				loop();
			});
		},

		/*
		 * ======================================================
		 * Functions for fetching user data from remote
		 */

		async fetchCurrentUserFromAWS() {
			try {
				// Force bypass cache to get fresh user data from Cognito
				const user = await getCurrentUser();
				const attributes = await fetchUserAttributes();
				this.$patch({
					currentAuthenticatedUserObject: {
						...user,
						attributes: attributes,
					} as any,
				});
				return user;
			} catch (error) {
				console.warn("file: user.ts:272 ~ fetchCurrentUserFromAWS ~ error:", error);
				return null;
			}
		},
		async fetchCurrentUserCustomizationsFromDB() {
			try {
				const currentUserID = await this.getCurrentUserID();
				// if for some reason currentUserID is still not available
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				const response = await sdk.users.getCustomization(currentUserID);
				this.currentUserCustomizations = response;

				if (this.currentUserCustomizations.Organizations) {
					posthog?.setPersonProperties({
						organizations: this.currentUserCustomizations.Organizations,
					});
				}

				if (this.currentUserCustomizations.AtomicNetworks) {
					posthog?.setPersonProperties({
						atomicNetworks: this.currentUserCustomizations.AtomicNetworks,
					});
				}
			} catch (error) {
				this.currentUserCustomizations = null;

				if (error instanceof SdkError) {
					if (error.code !== ERROR_CODES.API_NOT_FOUND) {
						monitoringCaptureError(error, "Fetch user customisations from DB");
					}
					return;
				}
				monitoringCaptureError(error, "Fetch user customisations from DB");
			}
		},

		async fetchCurrentUserObjectFromDB() {
			if (this.currentUserObjectFetchInFlight !== undefined) {
				return this.currentUserObjectFetchInFlight;
			}

			this.currentUserObjectFetchInFlight = (async () => {
				try {
					const currentUserID = await this.getCurrentUserID();
					if (!currentUserID) {
						throw new Error("User ID not found");
					}

					const response = await sdk.users.getOne(currentUserID);
					this.currentUserObjectFromDB = response;
					this.reportUserRecordShape(response);
				} catch (error) {
					monitoringCaptureError(error, USER_RECORD_TRANSACTION);
					console.log("Error fetching user data from DB :>> ", error);
					throw error;
				} finally {
					this.currentUserObjectFetchInFlight = undefined;
				}
			})();

			return this.currentUserObjectFetchInFlight;
		},
		async fetchCurrentUserSubscriptions() {
			try {
				const currentUserID = await this.getCurrentUserID();
				// if for some reason currentUserID is still not available
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				const response = await sdk.subscriptions.get();

				this.currentUserSubscriptions = response;
			} catch (error) {
				/*
				 *	We only catch this here to capture it in monitoring. We will
				 *	not handle this exception here.
				 */
				monitoringCaptureError(error, "Fetch user subscription");
				console.log("Error fetching user subscriptions :>> ", error);
				// re-throw error
				throw error;
			}
		},
		async fetchCurrentUserSessionFromAWS(): Promise<AWSAuthenticatedSession | null> {
			try {
				const session = await fetchAuthSession();
				this.currentUserSessionObject = session;
				return session;
			} catch {
				// User is not logged in - this is an expected state, not an error
				// Return null to indicate no session exists
				this.currentUserSessionObject = null;
				return null;
			}
		},
		async updateUserName(name: string): Promise<void> {
			await getCurrentUser();
			await updateUserAttributes({
				userAttributes: {
					name: name,
				},
			});
			await this.fetchCurrentUserFromAWS();
		},

		async syncUserPreferenceWithRemote(newPreferences: Partial<UserPreferences>): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				const newUserPreferencesData = await sdk.users.update(currentUserID, {
					Preferences: newPreferences,
				});
				const newUserPreferences = newUserPreferencesData.Preferences as UserPreferences;

				if (this.currentUserObjectFromDB) {
					this.currentUserObjectFromDB.Preferences = {
						...this.currentUserObjectFromDB.Preferences,
						...newUserPreferences,
					};
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync user preferences with DB");
				return false;
			}
		},
		async syncOnboardingStateWithRemote(newState: Partial<OnboardingState>): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				// The SDK does not yet type OnboardingState; it forwards arbitrary
				// fields to PATCH /users/{id}, which the server accepts. Cast both
				// sides to bridge the typing gap.
				const response = (await sdk.users.update(currentUserID, {
					OnboardingState: newState,
				} as unknown as Parameters<typeof sdk.users.update>[1])) as unknown as {
					OnboardingState?: Partial<OnboardingState>;
				};
				const updatedState = response.OnboardingState;

				if (this.currentUserObjectFromDB) {
					this.currentUserObjectFromDB.OnboardingState = {
						...this.currentUserObjectFromDB.OnboardingState,
						...updatedState,
					};
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync onboarding state with DB");
				return false;
			}
		},
		async syncUserProfileWithRemote(profile: { Role?: string | null; Industry?: string | null }): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				await sdk.users.update(currentUserID, profile);

				// The write is last-wins per key, so the request body is what the
				// server now holds. Keys the caller omitted stay untouched.
				if (this.currentUserObjectFromDB) {
					Object.assign(this.currentUserObjectFromDB, profile);
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync user profile (role/industry) with DB");
				return false;
			}
		},
		async startFreeTrial() {
			try {
				const response = await sdk.subscriptions.update({ Tier: "ProTier" }, { freeTrialOption: true });
				this.currentUserSubscriptions = response.subscription;
			} catch (error) {
				monitoringCaptureError(error, "Start Free Trial");
				console.log("Error starting free trial :>> ", error);
				throw error;
			}
		},

		async fetchCurrentUserActivityLogs(
			options: {
				startTime?: number;
				endTime?: number;
				limit?: number;
			} = {},
		) {
			this.activityLogsLoading = true;
			this.activityLogsError = null;

			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				const response = await sdk.users.getLogs(currentUserID, options);
				this.currentUserActivityLogs = response;
				return this.currentUserActivityLogs;
			} catch (error) {
				this.activityLogsError = error instanceof Error ? error.message : "Unknown error occurred";
				this.currentUserActivityLogs = undefined;

				if (error instanceof SdkError) {
					// We do not send 404 errors to sentry to reduce noise
					if (error.code !== ERROR_CODES.API_NOT_FOUND) {
						monitoringCaptureError(error, "Fetch user activity logs");
					}
					return null;
				}

				monitoringCaptureError(error, "Fetch user activity logs");
				return null;
			} finally {
				this.activityLogsLoading = false;
			}
		},
	},
});
